import { useLayoutEffect, useState } from 'react';

const FALLBACK_WIDTH = 360;
const FALLBACK_HEIGHT = 360;
const PADDING = 8;

export function useSmartPositioning({ open, triggerRef, containerRef, preferred = null }) {
   const [pickerStyles, setPickerStyles] = useState({});
   const [positionName, setPositionName] = useState(null);

   useLayoutEffect(() => {
      if (!open) return;

      const triggerEl = triggerRef?.current;
      const pickerEl = containerRef?.current;
      if (!triggerEl || !pickerEl) return;

      const updatePosition = () => {
         if (!triggerEl || !pickerEl) return;

         const measuredWidth = pickerEl.offsetWidth || FALLBACK_WIDTH;
         const measuredHeight = pickerEl.offsetHeight || FALLBACK_HEIGHT;

         const viewportWidth = window.innerWidth;
         const viewportHeight = window.innerHeight;

         const pickerWidth = Math.min(measuredWidth, viewportWidth - PADDING * 2);
         const pickerHeight = Math.min(measuredHeight, viewportHeight - PADDING * 2);

         const triggerRect = triggerEl.getBoundingClientRect();

         // Calculate center position
         const centerLeft = triggerRect.left + triggerRect.width / 2 - pickerWidth / 2;

         // Candidate positions
         const positions = [
            {
               name: 'bottom-right',
               top: triggerRect.top + window.scrollY + triggerRect.height,
               left: triggerRect.left,
            },
            {
               name: 'bottom-left',
               top: triggerRect.top + window.scrollY + triggerRect.height,
               left: triggerRect.right - pickerWidth,
            },
            {
               name: 'bottom-center',
               top: triggerRect.top + window.scrollY + triggerRect.height,
               left: centerLeft,
            },
            {
               name: 'top-right',
               top: triggerRect.top + window.scrollY - pickerHeight,
               left: triggerRect.left,
            },
            {
               name: 'top-left',
               top: triggerRect.top + window.scrollY - pickerHeight,
               left: triggerRect.right - pickerWidth,
            },
            {
               name: 'top-center',
               top: triggerRect.top + window.scrollY - pickerHeight,
               left: centerLeft,
            },
         ];

         // Filter by preferred side
         let filtered = positions;
         if (preferred === 'left') filtered = positions.filter(p => p.name.endsWith('left'));
         else if (preferred === 'right') filtered = positions.filter(p => p.name.endsWith('right'));
         else if (preferred === 'center') filtered = positions.filter(p => p.name.endsWith('center'));
         else if (preferred === 'top') filtered = positions.filter(p => p.name.startsWith('top'));
         else if (preferred === 'bottom') filtered = positions.filter(p => p.name.startsWith('bottom'));
         if (filtered.length === 0) filtered = positions;

         let finalTop = 0;
         let finalLeft = 0;
         let chosen = null;

         // Pick first that fully fits
         for (const pos of filtered) {
            const fits =
               pos.top >= PADDING &&
               pos.left >= PADDING &&
               pos.top + pickerHeight <= viewportHeight - PADDING &&
               pos.left + pickerWidth <= viewportWidth - PADDING;

            if (fits) {
               finalTop = pos.top;
               finalLeft = pos.left;
               chosen = pos.name;
               break;
            }
         }

         // Fallback: clamp within viewport
         if (!chosen) {
            const fallback = filtered[0];
            if (fallback) {
               finalTop = Math.max(PADDING, Math.min(fallback.top, viewportHeight - pickerHeight - PADDING));
               finalLeft = Math.max(PADDING, Math.min(fallback.left, viewportWidth - pickerWidth - PADDING));
               chosen = fallback.name;
            }
         }

         setPickerStyles({
            position: 'absolute',
            top: `${finalTop}px`,
            left: `${finalLeft}px`,
            maxWidth: `${viewportWidth - PADDING * 2}px`,
            maxHeight: `${viewportHeight - PADDING * 2}px`,
            overflowY: 'auto',
            zIndex: 12,
            visibility: 'visible',
            pointerEvents: 'auto',
         });
         setPositionName(chosen);
      };

      // Initial run
      updatePosition();

      // ResizeObserver for dynamic menu size changes
      const resizeObserver = new ResizeObserver(() => {
         requestAnimationFrame(updatePosition);
      });
      resizeObserver.observe(pickerEl);

      // Window events
      window.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', updatePosition, true);

      return () => {
         resizeObserver.disconnect();
         window.removeEventListener('resize', updatePosition);
         window.removeEventListener('scroll', updatePosition, true);
      };
   }, [open, triggerRef, containerRef, preferred]);

   return { pickerStyles, positionName };
}
